import {
  Button,
  ButtonGroup,
  FormControl,
  FormHelperText,
  InputLabel,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { camelCase, first, get, has, isObject, join } from 'lodash';
import { useCallback, useMemo } from 'react';

import { type InputComponent } from '..';

export type InputSelectBarOption =
  | {
      value: string | number;
      label: string;
      order?: number;
      disabled?: boolean;
    }
  | string
  | number;

export interface InputSelectBarProps extends InputComponent {
  options?: InputSelectBarOption[];
}

export function InputSelectBar({
  label,
  nameOverride,
  hint,
  disabled,
  required,
  options = [],
}: InputSelectBarProps) {
  const { setFieldValue, values, errors } =
    useFormikContext<Record<string, string>>();

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const selectedOption = useMemo(() => {
    if (has(values, name)) {
      return get(values, name);
    }
    return first(options);
  }, [name, options, values]);

  const handleSelect = useCallback(
    (option: InputSelectBarOption) =>
      setFieldValue(name, isObject(option) ? option.value : option),
    [name, setFieldValue],
  );

  return (
    <FormControl fullWidth sx={{ mt: 2 }}>
      {label && (
        <InputLabel shrink sx={{ top: -8, left: -8 }}>
          {label}
        </InputLabel>
      )}
      <ButtonGroup size={'small'} disableElevation>
        {options?.map((option, i) => (
          <Button
            disabled={disabled || (isObject(option) && option.disabled)}
            sx={{ textTransform: 'none', px: 2 }}
            key={i}
            variant={
              (isObject(option) ? option.value : option) === selectedOption
                ? 'contained'
                : 'outlined'
            }
            onClick={() => handleSelect(option)}
          >
            {isObject(option) ? option.label : option}
          </Button>
        ))}
      </ButtonGroup>
      {(!!errors[name] || !!hint) && (
        <FormHelperText error={!!errors[name]}>
          {errors[name] || hint}
        </FormHelperText>
      )}
    </FormControl>
  );
}
